'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { format } from 'date-fns';
import { MdArrowDropDown } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { BaseInput, FieldWrapper } from '@/components/ui/input';
import { CalendarPanel } from './CalendarPanel';
import { applyTimeToDate, dateToTimeValue } from './calendar-utils';
import type { TimeValue } from './calendar-utils';
import type { DatePickerProps } from './DatePicker.types';

/**
 * DatePicker component following the same FieldWrapper + BaseInput + popup pattern as Select.
 *
 * Features:
 * - Controlled (`value` + `onChange`) and uncontrolled (`defaultValue`) usage.
 * - Read-only input trigger with calendar popup for date selection.
 * - Desktop dropdown positioned below the trigger with flip logic.
 * - Mobile bottom-sheet calendar.
 * - minDate / maxDate / filterDate for disabling specific dates.
 * - Optional clear button via `clearable`.
 * - Optional time selection via `enableTime`.
 * - Full keyboard accessibility.
 */
export function DatePicker({
  value: controlledValue,
  onChange,
  defaultValue,
  dateFormat: dateFormatProp,
  placeholder,
  label,
  required,
  requiredMarker,
  requiredMessage,
  error,
  helperText,
  disabled,
  clearable = false,
  minDate,
  maxDate,
  filterDate,
  className = '',
  inputClassName = '',
  containerClassName = '',
  hideBottomPadding = false,
  id,
  monthYearOnly = false,
  yearOnly = false,
  enableTime: enableTimeProp = false,
  timeInterval,
  filterTime,
  onOpenChange,
  ownedSurfaceRefs,
  'data-cy': dataCy,
  menuZIndex,
}: DatePickerProps) {
  const t = useTranslations('DatePicker');
  const generatedId = useId();
  const inputId = id ?? `datepicker-input-${generatedId}`;
  const helperId = `${inputId}-helper`;

  // A year-only picker has no day to attach a time to.
  const enableTime = enableTimeProp && !yearOnly;

  // Determine the display format based on mode
  const dateFormat =
    dateFormatProp ??
    (yearOnly
      ? 'yyyy'
      : monthYearOnly
        ? 'MM/yyyy'
        : enableTime
          ? 'MM/dd/yyyy hh:mm a'
          : 'MM/dd/yyyy');

  const [uncontrolledValue, setUncontrolledValue] = useState<Date | null>(defaultValue ?? null);
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Internal time state (used to track time selection within the panel)
  const [timeValue, setTimeValue] = useState<TimeValue | null>(() => {
    const initial = controlledValue ?? defaultValue;
    return initial ? dateToTimeValue(initial) : null;
  });

  // Keep internal timeValue in sync when the controlled value changes externally
  useEffect(() => {
    if (controlledValue) {
      setTimeValue(dateToTimeValue(controlledValue));
    }
  }, [controlledValue]);

  const anchorRef = useRef<HTMLDivElement>(null);
  const baseInputContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipNextOpenRef = useRef(false);

  // Mirrors of `open` and `onOpenChange` read by the unmount cleanup below. Written from an
  // effect rather than during render: a ref write in the render body trips `react-hooks/refs`
  // ("Cannot access refs during render"), and the cleanup only ever needs the value as of the
  // last COMMITTED render, which is exactly what an undep'd effect leaves behind.
  const openRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    openRef.current = open;
    onOpenChangeRef.current = onOpenChange;
  });

  /**
   * Reports the close when an open picker is destroyed rather than dismissed (PB-5386).
   *
   * ## Invariants:
   * - Emits `false` on unmount if, and ONLY if, the picker was open. A picker that unmounts
   *   while closed must stay silent: consumers aggregate this callback across several pickers
   *   (`SearchBar` keeps one counter for three), so a spurious close releases a flag that a
   *   DIFFERENT picker legitimately holds.
   * - Both values are read through refs, and each ref guards a different failure:
   *   reading `open` from the closure would read the mount-time `false` and never fire at all;
   *   listing `onOpenChange` as a dependency would re-run the effect whenever the caller's
   *   identity changed, firing this cleanup mid-life and emitting the spurious close above.
   * - The dependency array MUST stay empty. With `[open]` React runs the cleanup on every
   *   open-to-closed transition, so a normal dismiss would emit `false` twice — once here and
   *   once from `handleClose`.
   * - Deliberately does NOT route through `handleClose`: that path schedules a
   *   `requestAnimationFrame` which focuses `inputRef`, and on unmount that node is going away.
   */
  useEffect(
    () => () => {
      if (openRef.current) onOpenChangeRef.current?.(false);
    },
    []
  );

  const resolvedPlaceholder = placeholder ?? t('selectDate');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const displayValue = value ? format(value, dateFormat) : '';

  const handleValidationChange = useCallback((error: string | null) => {
    setValidationError(error);
  }, []);

  const displayError =
    open && value
      ? null
      : typeof error === 'string'
        ? error
        : error === true
          ? ''
          : validationError;

  const handleOpen = useCallback(() => {
    if (disabled || open) return;
    if (skipNextOpenRef.current) {
      skipNextOpenRef.current = false;
      return;
    }
    setOpen(true);
    setOpenCount((prev) => prev + 1);
    onOpenChange?.(true);
  }, [disabled, open, onOpenChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Suppress the follow-up `click` of the same physical press that closed the
    // panel (mousedown-to-close then click-on-trigger would otherwise re-open).
    skipNextOpenRef.current = true;
    requestAnimationFrame(() => {
      // Disarm after the current event cycle so a *later* click opens on the
      // first try. Previously the flag stayed armed until the next handleOpen,
      // which then swallowed the user's next open click (e.g. after selecting a
      // month or dismissing by clicking elsewhere). (PB-3125)
      skipNextOpenRef.current = false;
      inputRef.current?.focus();
    });
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleSelect = useCallback(
    (date: Date) => {
      // If time is enabled and we have a time value, apply it to the selected date.
      let finalDate = date;
      if (enableTime && timeValue) {
        finalDate = applyTimeToDate(date, timeValue);
      }
      if (!isControlled) setUncontrolledValue(finalDate);
      onChange?.(finalDate);
    },
    [isControlled, onChange, enableTime, timeValue]
  );

  const handleTimeChange = useCallback(
    (newTime: TimeValue, pendingDate?: Date | null) => {
      setTimeValue(newTime);
      // Apply the time to the day the user clicked in the panel. `value` is only
      // committed on Done, so reading it here dropped an in-panel date change and
      // re-emitted the *old* day with the new time — which callers that clamp
      // (exit-must-be-after-enter) then rejected outright, losing both edits.
      const base = pendingDate ?? value;
      if (base) {
        const updated = applyTimeToDate(base, newTime);
        if (!isControlled) setUncontrolledValue(updated);
        onChange?.(updated);
      }
    },
    [value, isControlled, onChange]
  );

  const handleClear = useCallback(() => {
    skipNextOpenRef.current = true;
    // Disarm after the current event cycle (see handleClose) so clearing doesn't
    // leave the flag armed and swallow the next open click. (PB-3125)
    requestAnimationFrame(() => {
      skipNextOpenRef.current = false;
    });
    if (!isControlled) {
      setUncontrolledValue(null);
    }
    onChange?.(null);
    setTimeValue(null);
    setOpen(false);
    onOpenChange?.(false);
  }, [isControlled, onChange, onOpenChange]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleOpen();
      }
    },
    [disabled, handleOpen]
  );

  const hasSelection = value != null;
  const displayTitle = label ?? t('selectDateTitle');

  return (
    <div
      ref={anchorRef}
      className={`tw:relative tw:min-w-0 tw:w-full ${className}`.trim()}
      onClick={disabled ? undefined : handleOpen}
    >
      <FieldWrapper
        label={label}
        required={required || requiredMarker}
        error={displayError}
        helperText={helperText}
        hideBottomPadding={hideBottomPadding}
        id={inputId}
        helperId={helperId}
      >
        <BaseInput
          ref={inputRef}
          id={inputId}
          data-cy={dataCy}
          readOnly
          value={displayValue}
          placeholder={resolvedPlaceholder}
          required={open && value != null ? false : required}
          requiredMessage={requiredMessage}
          error={error}
          helperText={helperText}
          disabled={disabled}
          rightIcon={
            <MdArrowDropDown
              data-cy="date-picker-trigger"
              className="tw:h-5 tw:w-5"
              aria-hidden="true"
            />
          }
          onKeyDown={handleInputKeyDown}
          onClear={clearable && hasSelection && !disabled ? handleClear : undefined}
          clearAriaLabel={clearable && hasSelection && !disabled ? t('clearDate') : undefined}
          onValidationChange={handleValidationChange}
          inputClassName={`tw:cursor-pointer ${inputClassName}`.trim()}
          containerClassName={containerClassName}
          containerRef={baseInputContainerRef}
          // PB-5609: no aria-expanded here. The attribute is not permitted on a plain textbox
          // (axe `aria-allowed-attr`, critical), so assistive tech is entitled to ignore it —
          // it was never conveying the open state it appeared to. aria-haspopup IS supported on
          // textbox and stays, so the popup is still advertised. Conveying open/closed properly
          // would mean role="combobox" plus an aria-controls id threaded into both of
          // CalendarPanel's render branches, and the panel only exists in the DOM while open.
          aria-haspopup="dialog"
        />
      </FieldWrapper>
      <CalendarPanel
        key={openCount}
        open={open}
        onClose={handleClose}
        ownedSurfaceRefs={ownedSurfaceRefs}
        onSelect={handleSelect}
        selectedDate={value}
        minDate={minDate}
        maxDate={maxDate}
        filterDate={filterDate}
        anchorRef={baseInputContainerRef}
        title={displayTitle}
        monthYearOnly={monthYearOnly}
        yearOnly={yearOnly}
        enableTime={enableTime}
        timeValue={timeValue}
        onTimeChange={handleTimeChange}
        timeInterval={timeInterval}
        filterTime={filterTime}
        menuZIndex={menuZIndex}
      />
    </div>
  );
}
